import React from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import './OnboardingWizard.css';

const steps = ['פרטי בית הכנסת', 'צור את המסך הראשון'];

// Animated gold comet ring — identical to LoginDialog
const SpinCircle = ({ size, right, left, bottom, top, duration, direction, cometFrom }) => (
    <Box sx={{
        position: 'absolute',
        ...(right  !== undefined && { right  }),
        ...(left   !== undefined && { left   }),
        ...(top    !== undefined && { top, transform: 'translateY(-50%)' }),
        ...(bottom !== undefined && { bottom }),
        width: size, height: size,
        borderRadius: '50%',
        border: '1px solid rgba(154,114,9,0.18)',
        pointerEvents: 'none',
    }}>
        <Box sx={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: `conic-gradient(from ${cometFrom}deg, transparent 93%, rgba(154,114,9,0.1) 95%, rgba(218,165,32,0.5) 96.5%, rgba(255,230,80,0.9) 97.5%, rgba(218,165,32,0.5) 98.5%, rgba(154,114,9,0.08) 99.5%, transparent 100%)`,
            WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            mask:        'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            animation: `${direction} ${duration} linear infinite`,
        }} />
    </Box>
);

const OnboardingWizard = ({
    open,
    activeStep,
    onNext,
    onBack,
    onFinish,
    children,
    isNextDisabled = false,
    isLoading = false,
}) => {
    if (!open) return null;

    const isLastStep     = activeStep === steps.length - 1;
    const isTemplateStep = activeStep === steps.length - 1;

    return (
        <Box sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            bgcolor: '#1e3a5f',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            direction: 'rtl',
            fontFamily: "'Heebo', sans-serif",
            p: 2,
        }}>
            {/* ── Static dot grid ── */}
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(218,165,32,0.06) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
            }} />

            {/* ── 5 animated gold sparkle layers ── */}
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(218,165,32,0.22) 1.5px, transparent 1.5px)',
                backgroundSize: '112px 112px',
                animation: 'heroDotTwinkle 2.9s ease-in-out infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(200,155,20,0.16) 1.5px, transparent 1.5px)',
                backgroundSize: '84px 84px', backgroundPosition: '14px 28px',
                animation: 'heroDotTwinkle 3.7s ease-in-out 1.3s infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(255,210,60,0.25) 1.5px, transparent 1.5px)',
                backgroundSize: '140px 112px', backgroundPosition: '42px 14px',
                animation: 'heroDotTwinkle 4.4s ease-in-out 2.5s infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(218,165,32,0.18) 1px, transparent 1px)',
                backgroundSize: '196px 140px', backgroundPosition: '70px 56px',
                animation: 'heroDotTwinkle 5.1s ease-in-out 0.7s infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(255,200,30,0.14) 1px, transparent 1px)',
                backgroundSize: '168px 196px', backgroundPosition: '28px 84px',
                animation: 'heroDotTwinkle 3.3s ease-in-out 2.1s infinite',
            }} />

            {/* ── Top-right radial glow ── */}
            <Box sx={{
                position: 'absolute', top: 0, right: 0,
                width: '55vw', height: '70vh', pointerEvents: 'none',
                background: 'radial-gradient(ellipse at top right, rgba(154,114,9,0.2) 0%, transparent 60%)',
            }} />

            {/* ── Spinning comet circles ── */}
            <SpinCircle size="74vw" right="-14%" top="50%"   duration="22s" direction="spinCW"  cometFrom={0}   />
            <SpinCircle size="50vw" right="-7%"  top="50%"   duration="34s" direction="spinCCW" cometFrom={180} />
            <SpinCircle size="35vw" left="-8%"   bottom="-12%" duration="28s" direction="spinCW" cometFrom={270} />

            {/* ── Modal Card ── */}
            <Paper
                elevation={0}
                className="wizard-card-enter"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: '20px',
                    backgroundColor: 'rgba(255,255,255,0.97)',
                    border: '1px solid rgba(184,134,11,0.3)',
                    boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(154,114,9,0.1)',
                    width: isTemplateStep ? '90vw' : '720px',
                    maxWidth: isTemplateStep ? '1300px' : '720px',
                    height: isTemplateStep ? '85vh' : 'auto',
                    maxHeight: '92vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width 0.4s cubic-bezier(0.22,1,0.36,1), max-width 0.4s cubic-bezier(0.22,1,0.36,1)',
                }}
            >
                {/* Gold top accent bar */}
                <Box sx={{
                    height: '4px', flexShrink: 0,
                    background: 'linear-gradient(90deg, #9a7209 0%, #f5d060 50%, #9a7209 100%)',
                }} />

                <Box sx={{
                    p: isTemplateStep ? '20px 24px' : '32px 36px',
                    flex: 1, display: 'flex', flexDirection: 'column',
                    minHeight: 0, overflow: 'hidden',
                }}>
                    {/* Logo — only on details step */}
                    {!isTemplateStep && (
                        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                            <img
                                src="/inorai-logo.png"
                                alt="inorai"
                                className="wizard-logo-fadein"
                                style={{ width: 90, height: 'auto', objectFit: 'contain' }}
                            />
                        </Box>
                    )}

                    {/* ── Custom Stepper ── */}
                    <Box sx={{ mb: isTemplateStep ? 2 : 2, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {steps.map((label, index) => (
                                <React.Fragment key={label}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        {/* Step circle */}
                                        <Box sx={{
                                            width: 38, height: 38,
                                            borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: index < activeStep
                                                ? '#9a7209'
                                                : index === activeStep
                                                    ? '#1e3a5f'
                                                    : 'transparent',
                                            border: index < activeStep
                                                ? '2px solid #9a7209'
                                                : index === activeStep
                                                    ? '2px solid #1e3a5f'
                                                    : '2px solid #d4d8de',
                                            color: index <= activeStep ? '#fff' : '#c0c4cc',
                                            fontWeight: 700,
                                            fontSize: '0.875rem',
                                            mb: 0.75,
                                            fontFamily: "'Heebo', sans-serif",
                                            transition: 'all 0.3s ease',
                                            boxShadow: index === activeStep
                                                ? '0 4px 14px rgba(30,58,95,0.3)'
                                                : 'none',
                                        }}>
                                            {index < activeStep
                                                ? <CheckIcon sx={{ fontSize: 16 }} />
                                                : index + 1}
                                        </Box>
                                        {/* Step label */}
                                        <Typography sx={{
                                            fontSize: '0.76rem',
                                            fontWeight: index === activeStep ? 700 : 400,
                                            color: index === activeStep
                                                ? '#1e3a5f'
                                                : index < activeStep
                                                    ? '#9a7209'
                                                    : '#b0b8c4',
                                            textAlign: 'center',
                                            maxWidth: '110px',
                                            fontFamily: "'Heebo', sans-serif",
                                            transition: 'all 0.3s ease',
                                            lineHeight: 1.3,
                                        }}>
                                            {label}
                                        </Typography>
                                    </Box>

                                    {/* Connector line */}
                                    {index < steps.length - 1 && (
                                        <Box sx={{
                                            width: 90, height: 2,
                                            mx: 1.5, mb: '30px',
                                            background: index < activeStep
                                                ? 'linear-gradient(90deg, #9a7209, #f5d060)'
                                                : '#e4e8ee',
                                            borderRadius: 1,
                                            transition: 'background 0.4s ease',
                                        }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>

                    {/* ── Step Content ── */}
                    <Box sx={{
                        flex: 1,
                        minHeight: isTemplateStep ? 0 : 'auto',
                        overflow: isTemplateStep ? 'hidden' : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(154,114,9,0.25)',
                            borderRadius: 2,
                        },
                    }}>
                        {children}
                    </Box>

                    {/* ── Navigation Buttons ── */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: 2,
                        mt: 2,
                        flexShrink: 0,
                        borderTop: '1px solid rgba(0,0,0,0.07)',
                    }}>
                        <Button
                            onClick={onBack}
                            disabled={activeStep === 0 || isLoading}
                            sx={{
                                visibility: activeStep === 0 ? 'hidden' : 'visible',
                                color: '#1e3a5f',
                                fontWeight: 600,
                                fontFamily: "'Heebo', sans-serif",
                                fontSize: '0.9rem',
                                border: '1px solid rgba(30,58,95,0.2)',
                                borderRadius: '10px',
                                px: 2.5, py: 0.9,
                                textTransform: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'rgba(30,58,95,0.05)',
                                    borderColor: 'rgba(30,58,95,0.4)',
                                },
                            }}
                        >
                            → חזור
                        </Button>

                        <Button
                            variant="contained"
                            onClick={isLastStep ? onFinish : onNext}
                            disabled={isNextDisabled || isLoading}
                            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
                            sx={{
                                background: 'linear-gradient(135deg, #1e3a5f 0%, #2c4a6e 100%)',
                                color: '#fff',
                                fontWeight: 700,
                                px: 4, py: 1.1,
                                fontSize: '1rem',
                                borderRadius: '10px',
                                fontFamily: "'Heebo', sans-serif",
                                border: '1px solid rgba(184,134,11,0.4)',
                                boxShadow: '0 4px 20px rgba(30,58,95,0.35)',
                                textTransform: 'none',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #152a47 0%, #1e3a5f 100%)',
                                    boxShadow: '0 8px 30px rgba(30,58,95,0.5)',
                                    transform: 'translateY(-1px)',
                                    borderColor: 'rgba(184,134,11,0.7)',
                                },
                                '&:active': { transform: 'translateY(0)' },
                                '&.Mui-disabled': {
                                    background: 'rgba(30,58,95,0.2)',
                                    color: 'rgba(255,255,255,0.45)',
                                    border: '1px solid rgba(30,58,95,0.1)',
                                },
                            }}
                        >
                            {isLoading ? 'טוען...' : isLastStep ? 'סיים והתחל ←' : 'המשך ←'}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default OnboardingWizard;
